import DemoBanner from "./DemoBanner";
import { Leaf, TreePine, Gem, Phone, ArrowRight, Quote } from "lucide-react";
import { useState } from "react";

const services = [
  {
    icon: Leaf,
    title: "Lawn Care & Maintenance",
    desc: "Mowing, aeration, fertilization, and seasonal clean-ups. We keep your lawn looking its absolute best, year-round.",
  },
  {
    icon: Gem,
    title: "Hardscaping",
    desc: "Patios, retaining walls, fire pits, walkways, and outdoor living spaces. Built to last and designed to impress.",
  },
  {
    icon: TreePine,
    title: "Landscape Design",
    desc: "Full-service design from concept to installation. Plantings, grading, irrigation, lighting — the complete vision.",
  },
];

const projects = [
  {
    before: "https://images.unsplash.com/photo-1694629436281-35f7d2744711?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxvdmVyZ3Jvd24lMjBtZXNzeSUyMHlhcmQlMjBiZWZvcmUlMjByZW5vdmF0aW9ufGVufDB8fHx8MTc3NjI4OTIwMXww&ixlib=rb-4.1.0&q=85",
    after: "https://images.unsplash.com/photo-1687353813258-bf40a8d689f4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxiZWF1dGlmdWwlMjBtYWludGFpbmVkJTIwZ2FyZGVuJTIwbGFuZHNjYXBlJTIwZGVzaWduJTIwbHV4dXJ5fGVufDB8fHx8MTc3NjI4OTE1OXww&ixlib=rb-4.1.0&q=85",
    title: "Residential Garden Renovation",
    location: "Maple Heights",
  },
  {
    before: "https://images.unsplash.com/photo-1588120062347-30f1a6520cae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxvdmVyZ3Jvd24lMjBtZXNzeSUyMHlhcmQlMjBiZWZvcmUlMjByZW5vdmF0aW9ufGVufDB8fHx8MTc3NjI4OTIwMXww&ixlib=rb-4.1.0&q=85",
    after: "https://images.unsplash.com/photo-1762811054950-b74e0a055c80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBiYWNreWFyZCUyMHBhdGlvJTIwc3RvbmUlMjBoYXJkc2NhcGUlMjBvdXRkb29yJTIwbGl2aW5nfGVufDB8fHx8MTc3NjI4OTE2NXww&ixlib=rb-4.1.0&q=85",
    title: "Backyard Hardscape & Pool Patio",
    location: "Westlake",
  },
];

function BeforeAfterCard({ project }) {
  const [showAfter, setShowAfter] = useState(true);
  return (
    <div className="group">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={showAfter ? project.after : project.before}
          alt={showAfter ? "After" : "Before"}
          className="w-full h-full object-cover transition-all duration-700"
        />
        <div className="absolute top-4 left-4">
          <span className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 ${showAfter ? 'bg-[#1B3A2D] text-white' : 'bg-white text-[#2A2A2A]'}`}>
            {showAfter ? "After" : "Before"}
          </span>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div>
          <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl font-semibold text-[#1B3A2D]">
            {project.title}
          </h3>
          <p className="text-sm text-[#2A2A2A]/40 mt-0.5">{project.location}</p>
        </div>
        <button
          onClick={() => setShowAfter(!showAfter)}
          className="text-xs uppercase tracking-widest font-bold text-[#1B3A2D] border border-[#1B3A2D]/20 px-4 py-2 hover:bg-[#1B3A2D] hover:text-white transition-colors"
        >
          {showAfter ? "See Before" : "See After"}
        </button>
      </div>
    </div>
  );
}

export default function GreenLine() {
  return (
    <div data-testid="demo-greenline" style={{ fontFamily: "'DM Sans', sans-serif" }} className="bg-[#FAFAF7] text-[#2A2A2A] min-h-screen">

      {/* Nav */}
      <nav className="px-6 md:px-16 py-5 flex items-center justify-between border-b border-[#1B3A2D]/8">
        <div className="flex items-center gap-2.5">
          <div className="w-2.5 h-2.5 rounded-full bg-[#1B3A2D]" />
          <span style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-xl md:text-2xl font-semibold text-[#1B3A2D] tracking-tight">
            GreenLine
          </span>
          <span className="text-xs text-[#2A2A2A]/30 uppercase tracking-widest font-semibold hidden sm:inline ml-1">Landscaping</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-[#2A2A2A]/50">
          <a href="#services" className="hover:text-[#1B3A2D] transition-colors">Services</a>
          <a href="#work" className="hover:text-[#1B3A2D] transition-colors">Our Work</a>
          <a href="#contact" className="hover:text-[#1B3A2D] transition-colors">Contact</a>
          <button onClick={() => document.getElementById('contact')?.scrollIntoView({behavior:'smooth'})} className="bg-[#1B3A2D] text-white px-6 py-2.5 text-sm font-semibold hover:bg-[#C8A96E] hover:text-[#1B3A2D] transition-colors">
            Get a Quote
          </button>
        </div>
      </nav>

      {/* Hero — Split Layout */}
      <section className="grid grid-cols-1 lg:grid-cols-2 min-h-[85vh]">
        {/* Left */}
        <div className="flex flex-col justify-center px-6 md:px-16 lg:px-20 py-16 lg:py-0">
          <div className="flex items-center gap-2 mb-6">
            <div className="h-px w-10 bg-[#C8A96E]" />
            <span className="text-[#C8A96E] text-xs uppercase tracking-[0.3em] font-semibold">Premium Landscaping</span>
          </div>

          <h1 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#1B3A2D] leading-[1.05]">
            Landscapes
            <br />
            that <em className="text-[#C8A96E]">elevate</em>.
          </h1>

          <p className="text-base sm:text-lg text-[#2A2A2A]/50 mt-8 max-w-md leading-relaxed">
            We design and build outdoor spaces that transform how you live. From concept to completion, every detail is intentional.
          </p>

          <div className="flex flex-wrap gap-4 mt-10">
            <button onClick={() => document.getElementById('contact')?.scrollIntoView({behavior:'smooth'})} className="bg-[#1B3A2D] text-white px-8 py-4 text-sm font-semibold flex items-center gap-2 hover:bg-[#C8A96E] hover:text-[#1B3A2D] transition-colors">
              Start Your Project <ArrowRight size={16} />
            </button>
            <a href="tel:5558901234" className="border border-[#1B3A2D]/15 text-[#1B3A2D] px-8 py-4 text-sm font-semibold flex items-center gap-2 hover:border-[#1B3A2D]/40 transition-colors">
              <Phone size={16} /> (555) 890-1234
            </a>
          </div>

          <div className="flex items-center gap-8 mt-12 text-sm text-[#2A2A2A]/35 font-medium">
            <span>200+ Projects</span>
            <span className="w-1 h-1 rounded-full bg-[#2A2A2A]/20" />
            <span>12 Years Experience</span>
            <span className="w-1 h-1 rounded-full bg-[#2A2A2A]/20" />
            <span>Licensed & Insured</span>
          </div>
        </div>

        {/* Right — Hero Image */}
        <div className="relative overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1761637824120-cc56577081cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxiZWF1dGlmdWwlMjBtYWludGFpbmVkJTIwZ2FyZGVuJTIwbGFuZHNjYXBlJTIwZGVzaWduJTIwbHV4dXJ5fGVufDB8fHx8MTc3NjI4OTE1OXww&ixlib=rb-4.1.0&q=85"
            alt="Beautifully landscaped garden path"
            className="w-full h-full min-h-[400px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#FAFAF7] opacity-20 lg:opacity-30" />
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-24 md:py-32 px-6 md:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-px w-10 bg-[#C8A96E]" />
              <span className="text-[#C8A96E] text-xs uppercase tracking-[0.3em] font-semibold">What We Do</span>
              <div className="h-px w-10 bg-[#C8A96E]" />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl sm:text-5xl font-semibold text-[#1B3A2D]">
              Full-Service Landscaping
            </h2>
            <p className="text-[#2A2A2A]/45 mt-4 leading-relaxed">
              From weekly lawn care to complete outdoor transformations, we bring the same level of care and precision to every project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {services.map((s) => (
              <div key={s.title} className="text-center md:text-left">
                <div className="w-14 h-14 rounded-full bg-[#1B3A2D]/5 flex items-center justify-center mx-auto md:mx-0">
                  <s.icon size={24} className="text-[#1B3A2D]" />
                </div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl font-semibold text-[#1B3A2D] mt-5">
                  {s.title}
                </h3>
                <p className="text-[#2A2A2A]/45 mt-3 leading-relaxed text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-bleed photo break */}
      <div className="relative h-80 md:h-[500px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1694885090746-d90472e11c0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBiYWNreWFyZCUyMHBhdGlvJTIwc3RvbmUlMjBoYXJkc2NhcGUlMjBvdXRkb29yJTIwbGl2aW5nfGVufDB8fHx8MTc3NjI4OTE2NXww&ixlib=rb-4.1.0&q=85"
          alt="Outdoor patio hardscape"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Before/After */}
      <section id="work" className="py-24 md:py-32 px-6 md:px-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-px w-10 bg-[#C8A96E]" />
              <span className="text-[#C8A96E] text-xs uppercase tracking-[0.3em] font-semibold">Our Work</span>
              <div className="h-px w-10 bg-[#C8A96E]" />
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl sm:text-5xl font-semibold text-[#1B3A2D]">
              The Transformation
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14">
            {projects.map((p, i) => (
              <BeforeAfterCard key={i} project={p} />
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="bg-[#1B3A2D] py-20 md:py-28 px-6 md:px-16">
        <div className="max-w-3xl mx-auto text-center">
          <Quote size={32} className="text-[#C8A96E]/40 mx-auto mb-6" />
          <blockquote style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-2xl sm:text-3xl lg:text-4xl font-medium text-white/90 leading-snug italic">
            "GreenLine completely transformed our backyard. What used to be an afterthought is now where we spend every evening. The craftsmanship is unreal."
          </blockquote>
          <div className="mt-8">
            <p className="text-white/80 font-semibold">Sarah & David Holbrook</p>
            <p className="text-white/35 text-sm mt-1">Westlake Homeowners</p>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="py-24 md:py-32 px-6 md:px-16">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="h-px w-10 bg-[#C8A96E]" />
              <span className="text-[#C8A96E] text-xs uppercase tracking-[0.3em] font-semibold">Contact</span>
            </div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif" }} className="text-4xl sm:text-5xl font-semibold text-[#1B3A2D]">
              Let's talk about <em className="text-[#C8A96E]">your yard</em>.
            </h2>
            <p className="text-[#2A2A2A]/45 mt-6 leading-relaxed max-w-md">
              Tell us about your space and your vision. We'll handle the rest — from design to the final planting.
            </p>

            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-[#1B3A2D]" />
                <span className="font-semibold text-[#1B3A2D]">(555) 890-1234</span>
              </div>
              <p className="text-sm text-[#2A2A2A]/35 ml-8">Mon–Fri, 8am–6pm &middot; Sat by appointment</p>
            </div>
          </div>

          {/* Form */}
          <div>
            <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold text-[#2A2A2A]/50 mb-2 block">Name</label>
                  <input
                    type="text"
                    placeholder="Your name"
                    className="w-full px-4 py-3 bg-transparent border border-[#1B3A2D]/15 text-[#2A2A2A] text-sm focus:outline-none focus:border-[#1B3A2D] transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest font-semibold text-[#2A2A2A]/50 mb-2 block">Phone</label>
                  <input
                    type="tel"
                    placeholder="(555) 000-0000"
                    className="w-full px-4 py-3 bg-transparent border border-[#1B3A2D]/15 text-[#2A2A2A] text-sm focus:outline-none focus:border-[#1B3A2D] transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-[#2A2A2A]/50 mb-2 block">Email</label>
                <input
                  type="email"
                  placeholder="you@email.com"
                  className="w-full px-4 py-3 bg-transparent border border-[#1B3A2D]/15 text-[#2A2A2A] text-sm focus:outline-none focus:border-[#1B3A2D] transition-colors"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-semibold text-[#2A2A2A]/50 mb-2 block">Tell us about your project</label>
                <textarea
                  rows={4}
                  placeholder="What are you looking to do with your outdoor space?"
                  className="w-full px-4 py-3 bg-transparent border border-[#1B3A2D]/15 text-[#2A2A2A] text-sm resize-none focus:outline-none focus:border-[#1B3A2D] transition-colors"
                />
              </div>
              <button onClick={() => document.getElementById('contact')?.scrollIntoView({behavior:'smooth'})} className="bg-[#1B3A2D] text-white px-8 py-4 text-sm font-semibold hover:bg-[#C8A96E] hover:text-[#1B3A2D] transition-colors w-full sm:w-auto">
                Request a Consultation
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1B3A2D]/8 py-8 px-6 md:px-16 text-center">
        <span className="text-[#2A2A2A]/20 text-sm">
          {new Date().getFullYear()} GreenLine Landscaping. All rights reserved.
        </span>
      </footer>

      <div className="pb-16" />
      <DemoBanner />
    </div>
  );
}
